import { useState, useCallback } from "react";
import { toast } from "sonner";
import { db } from "@/services/firebaseService";
import { useAuth } from "@/context/AuthContext";
import { 
  copyVariationsFromLocation, 
  getLocationOptionsForOrg, 
  LocationOption,
  CopyVariationsResult 
} from "@/services/copyVariationsService";
import { CampLocation } from "@/types/campLocation";

interface UseCopyVariationsProps {
  location: CampLocation;
}

interface UseCopyVariationsReturn {
  isModalOpen: boolean;
  isLoading: boolean;
  locationOptions: LocationOption[];
  showCopyVariationsModal: () => Promise<void>;
  closeCopyVariationsModal: () => void;
  executeCopyVariations: (sourceLocationId: string) => Promise<void>;
}

export const useCopyVariations = ({ 
  location 
}: UseCopyVariationsProps): UseCopyVariationsReturn => {
  const { currentUser, userProfile } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);

  /**
   * Fetches location options and opens the copy modal
   */
  const showCopyVariationsModal = useCallback(async () => {
    if (!currentUser || !location) {
      toast.error("Missing required data for copy variations operation");
      return;
    }

    try {
      setIsLoading(true);
      console.log("[useCopyVariations] Fetching location options for org:", location.providedByOrg);
      
      // Get the organization ID from the location
      const orgId = typeof location.providedByOrg === 'object' 
        ? location.providedByOrg?.id 
        : location.providedByOrg;
      
      if (!orgId) {
        toast.error("Location has no organization ID");
        return;
      }

      // Fetch location options
      const options = await getLocationOptionsForOrg(db, orgId, location.id);
      setLocationOptions(options);
      setIsModalOpen(true);
      
      console.log(`[useCopyVariations] Found ${options.length} location options`);
    } catch (error) {
      console.error("[useCopyVariations] Error fetching location options:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to fetch location options: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, location]);

  /**
   * Closes the copy modal and resets state
   */
  const closeCopyVariationsModal = useCallback(() => {
    setIsModalOpen(false);
    setLocationOptions([]);
  }, []);

  /**
   * Executes the copy variations operation
   */
  const executeCopyVariations = useCallback(async (sourceLocationId: string) => {
    if (!currentUser || !location) {
      toast.error("Missing required data for copy variations operation");
      return;
    }

    try {
      setIsLoading(true);
      console.log("[useCopyVariations] Starting copy variations operation");
      
      const result: CopyVariationsResult = await copyVariationsFromLocation(
        db,
        sourceLocationId,
        location.id,
        currentUser.uid,
        userProfile?.role
      );

      console.log("[useCopyVariations] Copy variations completed:", result);

      // Show success message
      if (result.success) {
        const sourceLocation = locationOptions.find(opt => opt.id === sourceLocationId);
        const sourceName = sourceLocation?.name || 'selected location';
        
        toast.success(
          `Successfully copied ${result.copiedCount} program${result.copiedCount !== 1 ? 's' : ''} from ${sourceName}!`,
          {
            description: "The page will refresh to show the updated programs list."
          }
        );
        
        // Close modal
        closeCopyVariationsModal();
        
        // Refresh the page to show updated programs
        setTimeout(() => {
          window.location.reload();
        }, 1500); // Give time for the user to see the success message
        
      } else {
        toast.error(`Copy variations failed: ${result.errors.join(', ')}`);
      }

      // Log any errors
      if (result.errors.length > 0) {
        console.warn("[useCopyVariations] Copy variations errors:", result.errors);
      }
      
    } catch (error) {
      console.error("[useCopyVariations] Copy variations operation failed:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Copy variations failed: ${errorMessage}`);
      setIsLoading(false); // Reset loading state on error
    }
    // Note: Don't reset loading state on success since we're refreshing the page
  }, [currentUser, location, userProfile?.role, locationOptions, closeCopyVariationsModal]);

  return {
    isModalOpen,
    isLoading,
    locationOptions,
    showCopyVariationsModal,
    closeCopyVariationsModal,
    executeCopyVariations
  };
}; 